import React, { createContext, useContext, useEffect, useState } from 'react';
import { themeSchema, type Theme } from '@repo/shared-types/settings/schemas';
import { ROUTES } from '@repo/shared-types/routeRegistry';
import { logger } from '@repo/shared-utils';
import { useLocation } from '../../routes';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use lazy initialization to load theme from localStorage or system preference with validation
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      // ✅ Validate with Zod schema
      const result = themeSchema.safeParse(savedTheme);
      if (result.success) {
        return result.data;
      } else {
        logger.warn('Invalid theme setting, using default:', result.error);
        localStorage.removeItem('theme');
      }
    }
    // Fallback to system preference or default
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  useEffect(() => {
    // Apply theme class to html element
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev: Theme) => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const PUBLIC_THEME_LIGHT_PATHS = [
  ROUTES.ROOT,
  ROUTES.LEGAL_PRIVACY,
  ROUTES.LEGAL_TERMS,
  ROUTES.LEGAL_RETENTION,
  ROUTES.LEGAL_SUBPROCESSORS,
];

/**
 * Renders nothing; syncs document theme so public pages (landing, legal) always use light.
 * Must be mounted inside both ThemeProvider and RouterContext (e.g. inside AppRouter).
 */
export const PublicRouteThemeSync: React.FC = () => {
  const { pathname } = useLocation();
  const { theme } = useTheme();
  useEffect(() => {
    const isPublic = (PUBLIC_THEME_LIGHT_PATHS as readonly string[]).includes(pathname);
    const effective = isPublic ? 'light' : theme;
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(effective);
  }, [pathname, theme]);
  return null;
};


